import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash2, Edit, ChevronRight, ChevronDown, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ManageCodesPage = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCode, setEditCode] = useState(null);          // {id?, name, description}
  const [editValue, setEditValue] = useState(null);        // {codeId, value{id?, name, description, position, is_active}}
  const [expanded, setExpanded] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/system-codes');
      setCodes(data || []);
    } catch (e) { setCodes([]); }
    setLoading(false);
  };

  const saveCode = async () => {
    if (!editCode.name) { toast.error('Name required'); return; }
    try {
      if (editCode.id) await api.put(`/system-codes/${editCode.id}`, editCode);
      else await api.post('/system-codes', editCode);
      toast.success('Code saved');
      setEditCode(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteCode = async (code) => {
    if (code.system_defined) { toast.error('System-defined codes cannot be deleted'); return; }
    if (!window.confirm(`Delete code "${code.name}"?`)) return;
    try {
      await api.delete(`/system-codes/${code.id}`);
      toast.success('Code deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveValue = async () => {
    if (!editValue.value.name) { toast.error('Value name required'); return; }
    try {
      if (editValue.value.id) {
        await api.put(`/system-codes/${editValue.codeId}/values/${editValue.value.id}`, editValue.value);
      } else {
        await api.post(`/system-codes/${editValue.codeId}/values`, editValue.value);
      }
      toast.success('Value saved');
      setEditValue(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteValue = async (codeId, valueId) => {
    if (!window.confirm('Delete this value?')) return;
    try {
      await api.delete(`/system-codes/${codeId}/values/${valueId}`);
      toast.success('Value deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <AdminSubPage
      title="Manage Codes"
      breadcrumb={['Admin', 'System', 'Manage Codes']}
      backPath="/dashboard/admin/system"
      actionLabel="+ Add Code"
      onAction={() => setEditCode({ name: '', description: '' })}
    >
      <div className="bg-white border border-gray-200" data-testid="codes-table">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 w-8"></th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Code Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Description</th>
              <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Values</th>
              <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">System Defined</th>
              <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr>
              : codes.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-400 italic">No codes defined yet. Click "+ Add Code" to create one (e.g. <i>employment_status</i>, <i>marital_status</i>, <i>loan_purpose</i>, <i>decline_reason</i>).</td></tr>
              : codes.map(code => {
                const isOpen = expanded[code.id];
                const values = code.values || [];
                return (
                  <React.Fragment key={code.id}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3"><button onClick={() => setExpanded(p => ({ ...p, [code.id]: !p[code.id] }))} className="text-gray-400" data-testid={`toggle-${code.id}`}>
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button></td>
                      <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{code.name}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{code.description || '—'}</td>
                      <td className="px-4 py-3 text-center text-xs">{values.length}</td>
                      <td className="px-4 py-3 text-center">
                        {code.system_defined
                          ? <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 inline-flex items-center gap-1"><Lock className="w-3 h-3" />Yes</span>
                          : <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500">No</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setEditValue({ codeId: code.id, value: { name: '', description: '', position: 0, is_active: true } })} data-testid={`add-value-${code.id}`}>
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                        {!code.system_defined && <>
                          <Button variant="ghost" size="sm" onClick={() => setEditCode({ ...code })} data-testid={`edit-code-${code.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteCode(code)} className="text-red-500" data-testid={`delete-code-${code.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </>}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr><td colSpan={6} className="bg-gray-50 px-12 py-2">
                        {values.length === 0 ? <p className="text-xs text-gray-400 italic py-2">No values yet. Click + to add the first value.</p>
                          : <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                            {values.map(v => (
                              <div key={v.id} className="flex items-center justify-between bg-white p-2 border border-gray-200 text-xs" data-testid={`value-${v.id}`}>
                                <div className="flex-1">
                                  <span className={`font-medium ${v.is_active ? '' : 'text-gray-400 line-through'}`}>{v.name}</span>
                                  {v.description && <span className="text-gray-400 ml-2">— {v.description}</span>}
                                </div>
                                <div>
                                  <Button variant="ghost" size="sm" onClick={() => setEditValue({ codeId: code.id, value: { ...v } })}><Edit className="w-3 h-3" /></Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteValue(code.id, v.id)} className="text-red-500"><Trash2 className="w-3 h-3" /></Button>
                                </div>
                              </div>
                            ))}
                          </div>}
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Code edit modal */}
      {editCode && (
        <Dialog open={true} onOpenChange={() => setEditCode(null)}>
          <DialogContent aria-describedby="code-desc">
            <DialogHeader><DialogTitle>{editCode.id ? 'Edit Code' : 'Add Code'}</DialogTitle></DialogHeader>
            <p id="code-desc" className="sr-only">Code-value pair editor</p>
            <div className="space-y-3">
              <div>
                <Label>Code Name *</Label>
                <Input value={editCode.name || ''} onChange={e => setEditCode({ ...editCode, name: e.target.value })} placeholder="e.g. employment_status" data-testid="code-name-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editCode.description || ''} onChange={e => setEditCode({ ...editCode, description: e.target.value })} placeholder="Used in the New Client form" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditCode(null)}>Cancel</Button>
              <Button onClick={saveCode} className="bg-[#1f1f1f] text-white" data-testid="save-code-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Value edit modal */}
      {editValue && (
        <Dialog open={true} onOpenChange={() => setEditValue(null)}>
          <DialogContent aria-describedby="value-desc">
            <DialogHeader><DialogTitle>{editValue.value.id ? 'Edit Value' : 'Add Value'}</DialogTitle></DialogHeader>
            <p id="value-desc" className="sr-only">Code value editor</p>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={editValue.value.name || ''} onChange={e => setEditValue({ ...editValue, value: { ...editValue.value, name: e.target.value } })} placeholder="e.g. Self-employed" data-testid="value-name-input" /></div>
              <div><Label>Description</Label><Input value={editValue.value.description || ''} onChange={e => setEditValue({ ...editValue, value: { ...editValue.value, description: e.target.value } })} /></div>
              <div><Label>Position</Label><Input type="number" value={editValue.value.position || 0} onChange={e => setEditValue({ ...editValue, value: { ...editValue.value, position: parseInt(e.target.value) || 0 } })} /></div>
              <div className="flex items-center gap-2">
                <Switch checked={editValue.value.is_active !== false} onCheckedChange={v => setEditValue({ ...editValue, value: { ...editValue.value, is_active: v } })} />
                <Label>Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditValue(null)}>Cancel</Button>
              <Button onClick={saveValue} className="bg-[#1f1f1f] text-white" data-testid="save-value-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AdminSubPage>
  );
};

export default ManageCodesPage;
